<template>
  <div class="kds-container">
    <!-- TOP CONTROL BAR -->
    <div class="kds-header">
      <div class="header-left">
        <div class="kds-title">
          <span class="kitchen-emoji">👨‍🍳</span>
          <div>
            <h1>Bếp Chế Biến (KDS)</h1>
            <div class="live-status">
              <span class="status-dot" :class="{ connected: isConnected }"></span>
              <span>{{ isConnected ? 'Trực tuyến (Real-time)' : 'Đang kết nối lại...' }}</span>
              <span class="time-clock">⏰ {{ currentTimeFormatted }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- KDS METRICS -->
      <div class="kds-metrics">
        <div class="metric-card waiting-metric" :class="{ active: activeFilter === 'pending' }" @click="activeFilter = 'pending'">
          <div class="metric-num">{{ pendingTables.length }}</div>
          <div class="metric-label">Bàn chờ ({{ totalPendingDishes }} món)</div>
        </div>

        <div v-if="urgentCount > 0" class="metric-card urgent-metric" :class="{ active: activeFilter === 'urgent' }" @click="activeFilter = 'urgent'">
          <div class="metric-num">{{ urgentCount }}</div>
          <div class="metric-label">Chờ lâu (>15p) ⚠️</div>
        </div>

        <div class="metric-card done-metric" :class="{ active: activeFilter === 'served' }" @click="activeFilter = 'served'">
          <div class="metric-num">{{ servedTables.length }}</div>
          <div class="metric-label">Đã lên món</div>
        </div>
      </div>

      <!-- ACTIONS -->
      <div class="header-actions">
        <button
          class="kds-btn sound-btn"
          :class="{ muted: !soundEnabled }"
          @click="toggleSound"
          :title="soundEnabled ? 'Tắt âm báo' : 'Bật âm báo'"
        >
          <span>{{ soundEnabled ? '🔔 Âm báo: Bật' : '🔕 Âm báo: Tắt' }}</span>
        </button>

        <button
          class="kds-btn fullscreen-btn"
          @click="toggleFullscreen"
          title="Toàn màn hình"
        >
          <span>{{ isFullscreen ? '🗗 Thu nhỏ' : '⛶ Toàn màn hình' }}</span>
        </button>

        <button
          class="kds-btn refresh-btn"
          @click="loadOrders"
          title="Làm mới dữ liệu"
        >
          <span>🔄</span>
        </button>
      </div>
    </div>

    <!-- FILTER & SEARCH BAR -->
    <div class="kds-subbar">
      <div class="tab-pills">
        <button
          class="tab-pill"
          :class="{ active: activeFilter === 'all' }"
          @click="activeFilter = 'all'"
        >
          Tất cả đơn ({{ pendingTables.length + servedTables.length }})
        </button>
        <button
          class="tab-pill"
          :class="{ active: activeFilter === 'pending' }"
          @click="activeFilter = 'pending'"
        >
          ⏳ Đang chờ lên ({{ pendingTables.length }})
        </button>
        <button
          class="tab-pill"
          :class="{ active: activeFilter === 'served' }"
          @click="activeFilter = 'served'"
        >
          ✅ Đã xong hôm nay ({{ servedTables.length }})
        </button>
      </div>

      <div class="subbar-right">
        <div class="search-box">
          <span class="search-icon">🔍</span>
          <input
            v-model="searchQuery"
            placeholder="Tìm theo tên bàn hoặc món..."
            class="search-input"
          />
          <button v-if="searchQuery" class="clear-search" @click="searchQuery = ''">✕</button>
        </div>

        <div class="sort-select-wrapper">
          <select v-model="sortBy" class="sort-select">
            <option value="oldest">⏳ Chờ lâu nhất trước</option>
            <option value="newest">⚡ Mới nhất trước</option>
            <option value="table">🪑 Theo số bàn</option>
          </select>
        </div>
      </div>
    </div>

    <!-- ORDERS GRID -->
    <div class="kds-content">
      <!-- SECTION: CHỜ LÊN MÓN -->
      <div v-if="activeFilter !== 'served'" class="section-container">
        <div class="section-title-bar">
          <h2>
            ⏳ Đang chờ chế biến & lên món
            <span class="count-badge">{{ filteredPending.length }}</span>
          </h2>
          <span v-if="filteredPending.length > 0" class="sub-hint">
            Đầu bếp tick từng món hoặc bấm "Lên hết món" khi hoàn thành
          </span>
        </div>

        <!-- EMPTY STATE -->
        <div v-if="filteredPending.length === 0" class="empty-state">
          <div class="empty-icon">👨‍🍳✨</div>
          <h3>Không có món nào đang chờ chế biến!</h3>
          <p>Tất cả các món đã được lên đầy đủ cho khách.</p>
        </div>

        <!-- CARDS GRID -->
        <div v-else class="tickets-grid">
          <div
            v-for="order in filteredPending"
            :key="order.id"
            class="ticket-card"
            :class="[getTicketUrgency(order), { 'just-served': order.justServed }]"
          >
            <!-- TICKET HEADER -->
            <div class="ticket-header">
              <div class="table-brand">
                <span class="table-tag">{{ order.table?.name || 'Bàn ?' }}</span>
                <span class="order-id">#{{ order.id }}</span>
              </div>

              <div class="ticket-timers">
                <span
                  class="elapsed-badge"
                  :class="getElapsedClass(order.sortTime)"
                  :title="'Vào lúc: ' + formatTime(order.createdAt)"
                >
                  ⏱ {{ getElapsedTime(order.sortTime) }}
                </span>
                <span class="created-time">{{ formatTime(order.createdAt) }}</span>
              </div>
            </div>

            <!-- PROGRESS BAR -->
            <div class="progress-container">
              <div class="progress-info">
                <span>Tiến độ lên món:</span>
                <strong>{{ getServedCount(order) }} / {{ (order.items || []).length }} món</strong>
              </div>
              <div class="progress-bar-bg">
                <div
                  class="progress-bar-fill"
                  :style="{ width: getProgressPercent(order) + '%' }"
                ></div>
              </div>
            </div>

            <!-- ORDER NOTE -->
            <div v-if="order.note" class="ticket-note">
              <span class="note-icon">📝</span>
              <span class="note-text"><strong>Ghi chú:</strong> {{ order.note }}</span>
            </div>

            <!-- ITEMS LIST -->
            <div class="ticket-items">
              <div
                v-for="item in (order.items || [])"
                :key="item.id"
                class="ticket-item"
                :class="{ 'item-done': item.status_item }"
              >
                <div class="item-left">
                  <div class="item-qty-badge">x{{ item.qty }}</div>
                  <div class="item-details">
                    <span class="item-name">{{ item.food?.name || item.name || 'Món' }}</span>
                    <span v-if="item.note" class="item-specific-note">
                      ⚡ {{ item.note }}
                    </span>
                  </div>
                </div>

                <div class="item-right">
                  <button
                    v-if="!item.status_item"
                    class="item-action-btn"
                    @click="serveItem(order.id, item)"
                    title="Đánh dấu đã chế biến xong món này"
                  >
                    <span>✓ Xong</span>
                  </button>
                  <span v-else class="item-served-tag">
                    ✓ Đã lên
                  </span>
                </div>
              </div>
            </div>

            <!-- TICKET FOOTER -->
            <div class="ticket-footer">
              <button
                class="serve-all-btn"
                @click="serve(order)"
                title="Đánh dấu đã lên xong tất cả các món của bàn này"
              >
                <span>✅ Lên hết món (Hoàn thành)</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- SECTION: ĐÃ LÊN MÓN GẦN ĐÂY -->
      <div v-if="activeFilter === 'all' || activeFilter === 'served'" class="section-container served-section">
        <div class="section-title-bar">
          <h2>
            🍴 Đã lên món hoàn thành
            <span class="count-badge done">{{ filteredServed.length }}</span>
          </h2>
        </div>

        <div v-if="filteredServed.length === 0" class="empty-state small">
          <p>Chưa có bàn nào hoàn thành trong danh sách.</p>
        </div>

        <div v-else class="tickets-grid">
          <div
            v-for="order in filteredServed"
            :key="order.id"
            class="ticket-card ticket-done"
          >
            <div class="ticket-header done-header">
              <div class="table-brand">
                <span class="table-tag">{{ order.table?.name || 'Bàn ?' }}</span>
                <span class="order-id">#{{ order.id }}</span>
              </div>
              <span class="badge-done">ĐÃ PHỤC VỤ XONG</span>
            </div>

            <div class="ticket-items done-items">
              <div
                v-for="item in (order.items || [])"
                :key="item.id"
                class="done-item-row"
              >
                <span class="done-name">{{ item.food?.name || item.name }}</span>
                <span class="done-qty">x{{ item.qty }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { getOrderByTable, lenMon, serveOrderItems } from '../services/orders'
import { getWsUrl } from '../services/api'
import SockJS from 'sockjs-client'
import { Client } from '@stomp/stompjs'
import ting from '../assets/sounds/nhac_chuong_thong_bao_iphone-www_tiengdong_com.mp3'

let stompClient = null
const orders = ref([])
const isConnected = ref(false)
const isFullscreen = ref(false)
const activeFilter = ref('pending') // 'pending' | 'urgent' | 'served' | 'all'
const searchQuery = ref('')
const sortBy = ref('oldest') // 'oldest' | 'newest' | 'table'

// ==================== LIVE TIME & CLOCK ====================
const nowTimestamp = ref(Date.now())
let clockTimer = null

const currentTimeFormatted = computed(() => {
  const d = new Date(nowTimestamp.value)
  return d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
})

// ==================== ÂM THANH ====================
const soundEnabled = ref(true)
let soundDebounceTimeout = null

function toggleSound() {
  soundEnabled.value = !soundEnabled.value
}

function playNotification() {
  if (!soundEnabled.value) return
  try {
    const audio = new Audio(ting)
    audio.play().catch(() => {})
  } catch (e) {
    console.error('Audio play error', e)
  }
}

function disableSoundTemporarily() {
  soundEnabled.value = false
  if (soundDebounceTimeout) clearTimeout(soundDebounceTimeout)
  soundDebounceTimeout = setTimeout(() => {
    soundEnabled.value = true
  }, 1500)
}

function toggleFullscreen() {
  if (!document.fullscreenElement) {
    document.documentElement.requestFullscreen().catch(() => {})
    isFullscreen.value = true
  } else {
    document.exitFullscreen().catch(() => {})
    isFullscreen.value = false
  }
}

// ==================== LOAD DỮ LIỆU ====================
async function loadOrders() {
  try {
    const data = await getOrderByTable()
    orders.value = Array.isArray(data) ? data : []
  } catch (err) {
    console.error('Failed to load orders:', err)
    orders.value = []
  }
}

// ==================== WEBSOCKET ====================
onMounted(async () => {
  await loadOrders()

  // Bắt đầu đồng hồ live timer (1s/lần)
  clockTimer = setInterval(() => {
    nowTimestamp.value = Date.now()
  }, 1000)

  document.addEventListener('fullscreenchange', () => {
    isFullscreen.value = !!document.fullscreenElement
  })

  // Khởi tạo SockJS + STOMP
  stompClient = new Client({
    webSocketFactory: () => new SockJS(getWsUrl('/ws')),
    reconnectDelay: 3000,
    debug: () => {},

    onConnect: () => {
      isConnected.value = true
      stompClient.subscribe('/topic/orders', (message) => {
        console.log('KDS event received:', message.body)
        playNotification()
        loadOrders()
      })
    },

    onDisconnect: () => {
      isConnected.value = false
    },

    onStompError: () => {
      isConnected.value = false
    }
  })

  stompClient.activate()
})

onUnmounted(() => {
  if (clockTimer) clearInterval(clockTimer)
  if (stompClient) stompClient.deactivate()
  if (soundDebounceTimeout) clearTimeout(soundDebounceTimeout)
})

// ==================== COMPUTED TICKETS ====================

// Danh sách các bàn đang chờ lên món
const pendingTables = computed(() => {
  if (!Array.isArray(orders.value)) return []
  return orders.value
    .map(o => {
      if (!o || !Array.isArray(o.items)) return null
      const pendingItems = o.items.filter(it => it && !it.status_item)
      if (pendingItems.length === 0) return null

      const hasDoneItem = o.items.some(it => it && it.status_item)
      const latestPendingTime = Math.max(
        ...pendingItems.map(it => new Date(it.createdAt || o.createdAt || Date.now()).getTime())
      )

      return {
        ...o,
        pendingItems,
        sortTime: hasDoneItem ? latestPendingTime : new Date(o.createdAt || Date.now()).getTime()
      }
    })
    .filter(Boolean)
})

// Danh sách các bàn đã xong (status 2)
const servedTables = computed(() => {
  if (!Array.isArray(orders.value)) return []
  return orders.value.filter(o => o && o.table && o.table.status === '2')
})

// Tổng số món đang cần nấu/lên
const totalPendingDishes = computed(() => {
  return pendingTables.value.reduce((total, order) => {
    const pItems = (order.items || []).filter(i => !i.status_item)
    return total + pItems.reduce((s, it) => s + (it.qty || 1), 0)
  }, 0)
})

// Số đơn chờ > 15 phút
const urgentCount = computed(() => {
  const fifteenMins = 15 * 60 * 1000
  return pendingTables.value.filter(o => (nowTimestamp.value - o.sortTime) >= fifteenMins).length
})

// Lọc và sắp xếp danh sách chờ
const filteredPending = computed(() => {
  let list = [...pendingTables.value]

  // Filter urgent
  if (activeFilter.value === 'urgent') {
    const fifteenMins = 15 * 60 * 1000
    list = list.filter(o => (nowTimestamp.value - o.sortTime) >= fifteenMins)
  }

  // Filter Search
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase().trim()
    list = list.filter(o => {
      const tableName = (o.table?.name || '').toLowerCase()
      const matchItems = (o.items || []).some(i => (i.food?.name || i.name || '').toLowerCase().includes(q))
      const matchNote = (o.note || '').toLowerCase().includes(q)
      return tableName.includes(q) || matchItems || matchNote
    })
  }

  // Sắp xếp
  if (sortBy.value === 'oldest') {
    list.sort((a, b) => a.sortTime - b.sortTime)
  } else if (sortBy.value === 'newest') {
    list.sort((a, b) => b.sortTime - a.sortTime)
  } else if (sortBy.value === 'table') {
    list.sort((a, b) => (a.table?.name || '').localeCompare(b.table?.name || '', undefined, { numeric: true }))
  }

  return list
})

// Lọc danh sách đã xong
const filteredServed = computed(() => {
  let list = [...servedTables.value]
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.toLowerCase().trim()
    list = list.filter(o => {
      const tableName = (o.table?.name || '').toLowerCase()
      const matchItems = (o.items || []).some(i => (i.food?.name || i.name || '').toLowerCase().includes(q))
      return tableName.includes(q) || matchItems
    })
  }
  return list
})

// ==================== HELPER FUNCTIONS ====================

function formatTime(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
}

function getElapsedTime(timestamp) {
  if (!timestamp) return '00:00'
  const seconds = Math.max(0, Math.floor((nowTimestamp.value - timestamp) / 1000))
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (mins >= 60) {
    const hours = Math.floor(mins / 60)
    const remMins = mins % 60
    return `${hours}h ${remMins}p`
  }
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

function getElapsedClass(timestamp) {
  if (!timestamp) return 'time-normal'
  const seconds = Math.floor((nowTimestamp.value - timestamp) / 1000)
  if (seconds >= 900) return 'time-urgent' // >= 15 min
  if (seconds >= 480) return 'time-warning' // >= 8 min
  return 'time-normal'
}

function getTicketUrgency(order) {
  const seconds = Math.floor((nowTimestamp.value - order.sortTime) / 1000)
  if (seconds >= 900) return 'ticket-urgent'
  if (seconds >= 480) return 'ticket-warning'
  return 'ticket-normal'
}

function getServedCount(order) {
  if (!order || !Array.isArray(order.items)) return 0
  return order.items.filter(i => i && i.status_item).length
}

function getProgressPercent(order) {
  if (!order || !Array.isArray(order.items) || order.items.length === 0) return 0
  const served = getServedCount(order)
  return Math.round((served / order.items.length) * 100)
}

// ==================== ACTIONS ====================

async function serve(order) {
  disableSoundTemporarily()
  try {
    await lenMon(order.id)
    await loadOrders()
  } catch (e) {
    console.error('Serve order error:', e)
  }
}

async function serveItem(orderId, item) {
  disableSoundTemporarily()
  try {
    await serveOrderItems(orderId, item.id)

    const targetOrder = orders.value.find(o => o.id === orderId)
    if (targetOrder && Array.isArray(targetOrder.items)) {
      const targetItem = targetOrder.items.find(i => i.id === item.id)
      if (targetItem) targetItem.status_item = true

      targetOrder.justServed = true
      setTimeout(() => {
        targetOrder.justServed = false
      }, 1500)

      const hasPending = targetOrder.items.some(i => !i.status_item)
      if (!hasPending) {
        await lenMon(orderId)
      }
    }

    await loadOrders()
  } catch (e) {
    console.error('Serve item error', e)
  }
}
</script>

<style scoped>
/* MAIN CONTAINER */
.kds-container {
  min-height: calc(100vh - 48px);
  background-color: #0f172a;
  color: #f8fafc;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  display: flex;
  flex-direction: column;
}

/* TOP HEADER */
.kds-header {
  background: #1e293b;
  padding: 12px 20px;
  border-bottom: 2px solid #334155;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.kds-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.kitchen-emoji {
  font-size: 32px;
}

.kds-title h1 {
  font-size: 1.25rem;
  font-weight: 800;
  color: #ffffff;
  margin: 0;
  letter-spacing: 0.01em;
}

.live-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.82rem;
  color: #94a3b8;
  margin-top: 3px;
}

.status-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background-color: #ef4444;
  box-shadow: 0 0 8px #ef4444;
}

.status-dot.connected {
  background-color: #22c55e;
  box-shadow: 0 0 8px #22c55e;
}

.time-clock {
  color: #38bdf8;
  font-weight: 600;
  margin-left: 8px;
  background: rgba(56, 189, 248, 0.1);
  padding: 2px 8px;
  border-radius: 6px;
}

/* METRICS */
.kds-metrics {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.metric-card {
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 10px;
  padding: 6px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 90px;
}

.metric-card:hover {
  transform: translateY(-2px);
  border-color: #64748b;
}

.metric-card.active {
  border-color: #ff6b35;
  background: rgba(255, 107, 53, 0.12);
}

.waiting-metric .metric-num {
  color: #fbbf24;
  font-size: 1.35rem;
  font-weight: 800;
}

.urgent-metric .metric-num {
  color: #f87171;
  font-size: 1.35rem;
  font-weight: 800;
  animation: pulse 1.5s infinite;
}

.done-metric .metric-num {
  color: #4ade80;
  font-size: 1.35rem;
  font-weight: 800;
}

.metric-label {
  font-size: 0.72rem;
  color: #94a3b8;
  font-weight: 600;
}

/* HEADER ACTIONS */
.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.kds-btn {
  background: #334155;
  color: #f1f5f9;
  border: 1px solid #475569;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.kds-btn:hover {
  background: #475569;
}

.sound-btn.muted {
  background: rgba(239, 68, 68, 0.2);
  border-color: #ef4444;
  color: #fca5a5;
}

/* SUBBAR / TABS & SEARCH */
.kds-subbar {
  background: #1e293b;
  padding: 8px 20px;
  border-bottom: 1px solid #334155;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.tab-pills {
  display: flex;
  gap: 6px;
}

.tab-pill {
  background: transparent;
  color: #94a3b8;
  border: 1px solid transparent;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-pill:hover {
  color: #ffffff;
  background: #334155;
}

.tab-pill.active {
  background: #ff6b35;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(255, 107, 53, 0.35);
}

.subbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 10px;
  font-size: 0.85rem;
  color: #64748b;
}

.search-input {
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 8px;
  padding: 6px 30px 6px 32px;
  color: #ffffff;
  font-size: 0.85rem;
  width: 220px;
  outline: none;
  transition: all 0.2s;
}

.search-input:focus {
  border-color: #38bdf8;
  width: 260px;
}

.clear-search {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  font-size: 0.85rem;
}

.sort-select {
  background: #0f172a;
  border: 1px solid #334155;
  color: #f1f5f9;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 0.85rem;
  outline: none;
  cursor: pointer;
}

/* CONTENT AREA */
.kds-content {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
}

.section-container {
  margin-bottom: 30px;
}

.section-title-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.section-title-bar h2 {
  font-size: 1.15rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.count-badge {
  background: #f59e0b;
  color: #0f172a;
  font-size: 0.85rem;
  font-weight: 800;
  padding: 2px 10px;
  border-radius: 999px;
}

.count-badge.done {
  background: #22c55e;
  color: #0f172a;
}

.sub-hint {
  font-size: 0.82rem;
  color: #94a3b8;
}

/* TICKETS GRID */
.tickets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 18px;
}

/* TICKET CARD */
.ticket-card {
  background: #1e293b;
  border-radius: 16px;
  border: 2px solid #334155;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
}

.ticket-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45);
}

.ticket-normal {
  border-top: 6px solid #38bdf8;
}

.ticket-warning {
  border-top: 6px solid #f59e0b;
  border-color: #b45309;
}

.ticket-urgent {
  border-top: 6px solid #ef4444;
  border-color: #dc2626;
  animation: border-pulse 2s infinite ease-in-out;
}

.ticket-card.just-served {
  background: #164e63;
}

/* TICKET HEADER */
.ticket-header {
  padding: 14px 16px;
  background: #0f172a;
  border-bottom: 1px solid #334155;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-brand {
  display: flex;
  align-items: center;
  gap: 8px;
}

.table-tag {
  font-size: 1.15rem;
  font-weight: 800;
  color: #ffffff;
  background: #334155;
  padding: 4px 10px;
  border-radius: 8px;
  letter-spacing: 0.02em;
}

.order-id {
  color: #94a3b8;
  font-size: 0.85rem;
  font-weight: 600;
}

.ticket-timers {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.elapsed-badge {
  font-family: 'JetBrains Mono', 'Roboto Mono', monospace;
  font-size: 0.95rem;
  font-weight: 800;
  padding: 3px 10px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
}

.time-normal {
  background: rgba(56, 189, 248, 0.18);
  color: #38bdf8;
  border: 1px solid rgba(56, 189, 248, 0.4);
}

.time-warning {
  background: rgba(245, 158, 11, 0.2);
  color: #fbbf24;
  border: 1px solid #f59e0b;
}

.time-urgent {
  background: #ef4444;
  color: #ffffff;
  box-shadow: 0 0 10px rgba(239, 68, 68, 0.6);
}

.created-time {
  font-size: 0.72rem;
  color: #94a3b8;
}

/* PROGRESS BAR */
.progress-container {
  padding: 10px 16px 6px 16px;
  background: #1e293b;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  font-size: 0.78rem;
  color: #cbd5e1;
  margin-bottom: 4px;
}

.progress-bar-bg {
  height: 6px;
  background: #334155;
  border-radius: 999px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #22c55e);
  transition: width 0.3s ease;
}

/* TICKET NOTE */
.ticket-note {
  margin: 8px 14px 4px 14px;
  padding: 8px 12px;
  background: rgba(245, 158, 11, 0.15);
  border-left: 4px solid #f59e0b;
  border-radius: 6px;
  color: #fde047;
  font-size: 0.85rem;
  display: flex;
  gap: 6px;
}

.note-icon {
  font-size: 1rem;
}

.note-text {
  line-height: 1.35;
}

/* ITEMS LIST */
.ticket-items {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.ticket-item {
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
}

.ticket-item:hover {
  border-color: #475569;
}

.ticket-item.item-done {
  background: rgba(16, 185, 129, 0.08);
  border-color: rgba(16, 185, 129, 0.3);
  opacity: 0.65;
}

.item-left {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
}

.item-qty-badge {
  background: #ff6b35;
  color: #ffffff;
  font-weight: 800;
  font-size: 1rem;
  padding: 4px 10px;
  border-radius: 8px;
  min-width: 32px;
  text-align: center;
}

.item-done .item-qty-badge {
  background: #334155;
  color: #94a3b8;
}

.item-details {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.item-name {
  font-size: 0.98rem;
  font-weight: 700;
  color: #ffffff;
  line-height: 1.3;
}

.item-done .item-name {
  text-decoration: line-through;
  color: #94a3b8;
}

.item-specific-note {
  font-size: 0.8rem;
  color: #facc15;
  font-weight: 600;
}

.item-action-btn {
  background: #10b981;
  color: #ffffff;
  border: none;
  padding: 8px 14px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.item-action-btn:hover {
  background: #059669;
  transform: scale(1.05);
}

.item-action-btn:active {
  transform: scale(0.95);
}

.item-served-tag {
  color: #4ade80;
  font-weight: 700;
  font-size: 0.82rem;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 4px 8px;
  background: rgba(34, 197, 94, 0.15);
  border-radius: 6px;
}

/* FOOTER */
.ticket-footer {
  padding: 12px 14px;
  background: #0f172a;
  border-top: 1px solid #334155;
}

.serve-all-btn {
  width: 100%;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  color: #ffffff;
  border: none;
  padding: 12px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
}

.serve-all-btn:hover {
  background: linear-gradient(135deg, #047857 0%, #059669 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.35);
}

.serve-all-btn:active {
  transform: translateY(0);
}

/* SERVED / DONE TICKETS */
.ticket-done {
  border-top: 6px solid #10b981;
  opacity: 0.85;
}

.done-header {
  background: #064e3b;
}

.badge-done {
  background: #10b981;
  color: #ffffff;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
}

.done-items {
  gap: 6px;
}

.done-item-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 10px;
  background: #0f172a;
  border-radius: 6px;
  font-size: 0.88rem;
}

.done-name {
  color: #cbd5e1;
}

.done-qty {
  font-weight: 700;
  color: #38bdf8;
}

/* EMPTY STATES */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: #1e293b;
  border-radius: 16px;
  border: 2px dashed #334155;
  margin-top: 20px;
}

.empty-icon {
  font-size: 56px;
  margin-bottom: 12px;
}

.empty-state h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 6px;
}

.empty-state p {
  color: #94a3b8;
  font-size: 0.92rem;
}

.empty-state.small {
  padding: 30px;
}

/* ANIMATIONS */
@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
}

@keyframes border-pulse {
  0%, 100% { border-color: #ef4444; }
  50% { border-color: #f87171; box-shadow: 0 0 16px rgba(239, 68, 68, 0.4); }
}

/* RESPONSIVE */
@media (max-width: 768px) {
  .kds-header {
    flex-direction: column;
    align-items: stretch;
    padding: 12px 14px;
  }

  .kds-metrics {
    justify-content: space-between;
  }

  .header-actions {
    justify-content: flex-end;
  }

  .kds-subbar {
    flex-direction: column;
    align-items: stretch;
    padding: 10px 14px;
  }

  .subbar-right {
    flex-direction: column;
    align-items: stretch;
  }

  .search-input {
    width: 100%;
  }

  .search-input:focus {
    width: 100%;
  }

  .tickets-grid {
    grid-template-columns: 1fr;
  }
}
</style>
